import { RECOMMENDED, REQUIRED, t, te } from "../types.ts";
import type {
  ChecklistItem,
  ChecklistSectionConfig,
  TranslatedText,
} from "../types.ts";

function item(
  requirement: TranslatedText,
  required: TranslatedText,
  details: TranslatedText,
  description: TranslatedText = te(""),
): ChecklistItem {
  return { requirement, required, details, description };
}

export const standardization: ChecklistSectionConfig = {
  id: "standardization",
  gradient: "from-indigo-800 to-indigo-900",
  hoverBg: "indigo-700",
  title: t("Standardization", "Standartizācija", "Standardizzazione"),
  tagline: t(
    'Refining the "Human" process before touching technology',
    "'Cilvēka' procesa uzlabošana pirms tehnoloģiju izmantošanas",
    'Raffinare il processo "Umano" prima di toccare la tecnologia',
  ),
  subsections: [
    {
      title: t(
        "1.1. Process Definition",
        "1.1. Procesa Definīcija",
        "1.1. Definizione del Processo",
      ),
      items: [
        item(
          t("Set Boundaries", "Nosakiet Robežas", "Definire i Confini"),
          REQUIRED,
          t(
            "Is it clear where the process triggers (starts) and where it officially ends?",
            "Vai ir skaidrs, kur process sākas un kur tas oficiāli beidzas?",
            "È chiaro dove il processo si avvia e dove termina ufficialmente?",
          ),
          t(
            "e.g., trigger: 'New invoice received'; end: 'Payment confirmed in accounting system'.",
            "piem., sākums: 'Saņemts jauns rēķins'; beigas: 'Maksājums apstiprināts grāmatvedības sistēmā'.",
            "es., avvio: 'Nuova fattura ricevuta'; fine: 'Pagamento confermato nel sistema contabile'.",
          ),
        ),
        item(
          t(
            "Knowledge Alignment",
            "Zināšanu Saskaņošana",
            "Allineamento delle Conoscenze",
          ),
          REQUIRED,
          t(
            "Does everyone involved understand why this process exists, even if steps aren't fully documented yet?",
            "Vai visi iesaistītie saprot, kāpēc šis process pastāv, pat ja soļi vēl nav pilnībā dokumentēti?",
            "Tutti i coinvolti capiscono perché esiste questo processo, anche se i passaggi non sono ancora completamente documentati?",
          ),
          t(
            "Run a quick team sync or FAQ doc to capture tribal knowledge before formalizing.",
            "Veiciet ātru komandas sinhronizāciju vai FAQ dokumentu, lai iegūtu neformālās zināšanas pirms formalizēšanas.",
            "Eseguire una rapida sincronizzazione del team o un documento FAQ per catturare la conoscenza tribale prima di formalizzare.",
          ),
        ),
        item(
          t(
            "Identify Owners",
            "Identificējiet Īpašniekus",
            "Identificare i Responsabili",
          ),
          REQUIRED,
          t(
            "Is it clear who is responsible for the overall success of this process and has decision rights?",
            "Vai ir skaidrs, kurš ir atbildīgs par šī procesa kopējo panākumu un kam ir lēmumu tiesības?",
            "È chiaro chi è responsabile del successo complessivo di questo processo e ha i diritti decisionali?",
          ),
          t(
            "Assign a single process owner; avoid shared ownership without clear escalation paths.",
            "Piešķiriet vienu procesa īpašnieku; izvairieties no kopīgas atbildības bez skaidriem eskalācijas ceļiem.",
            "Assegnare un unico responsabile del processo; evitare la proprietà condivisa senza percorsi di escalation chiari.",
          ),
        ),
        item(
          t("Role Assignment", "Lomu Piešķiršana", "Assegnazione dei Ruoli"),
          REQUIRED,
          t(
            "Is it clearly defined who executes which step within the documentation?",
            "Vai dokumentācijā ir skaidri noteikts, kurš izpilda kuru soli?",
            "È chiaramente definito chi esegue quale passaggio nella documentazione?",
          ),
          t(
            "Use a RACI matrix or inline role tags (e.g., [Finance], [Manager]) inside the SOP.",
            "Izmantojiet RACI matricu vai iekļautas lomu atzīmes (piem., [Finanses], [Vadītājs]) SOP iekšpusē.",
            "Usare una matrice RACI o tag di ruolo inline (es., [Finance], [Manager]) all'interno del SOP.",
          ),
        ),
        item(
          t("Goals & KPIs", "Mērķi un KPI", "Obiettivi e KPI"),
          REQUIRED,
          t(
            "Does the process have a clear purpose, a measurable result, and agreed-upon KPIs?",
            "Vai procesam ir skaidrs mērķis, izmērāms rezultāts un saskaņoti KPI?",
            "Il processo ha uno scopo chiaro, un risultato misurabile e KPI concordati?",
          ),
          t(
            "e.g., 'Invoice approved within 24h'; define success metrics before documenting steps.",
            "piem., 'Rēķins apstiprināts 24h laikā'; definējiet panākumu rādītājus pirms soļu dokumentēšanas.",
            "es., 'Fattura approvata entro 24h'; definire le metriche di successo prima di documentare i passaggi.",
          ),
        ),
      ],
    },
    {
      title: t(
        "1.2. Documentation",
        "1.2. Dokumentācija",
        "1.2. Documentazione",
      ),
      items: [
        item(
          t(
            "Process Documentation & SOPs",
            "Procesa Dokumentācija un SOP",
            "Documentazione del Processo e SOP",
          ),
          REQUIRED,
          t(
            "Is the process described in writing or flowcharts, formalized into a Standard Operating Procedure?",
            "Vai process ir aprakstīts rakstiski vai blokshēmās, formalizēts Standarta darbības procedūrā?",
            "Il processo è descritto per iscritto o in diagrammi di flusso, formalizzato in una Procedura Operativa Standard?",
          ),
          t(
            "Use Loom videos, Notion pages, or Lucidchart diagrams; link them from a single index.",
            "Izmantojiet Loom videoklipus, Notion lapas vai Lucidchart diagrammas; saistiet tos no viena indeksa.",
            "Usare video Loom, pagine Notion o diagrammi Lucidchart; collegarli da un unico indice.",
          ),
        ),
        item(
          t(
            "Enforce Adherence",
            "Nodrošiniet Ievērošanu",
            "Garantire il Rispetto",
          ),
          REQUIRED,
          t(
            "Is the team following the documented steps, or is there still an 'each to their own' approach?",
            "Vai komanda ievēro dokumentētos soļus, vai joprojām ir 'katrs pats par sevi' pieeja?",
            "Il team segue i passaggi documentati, o c'è ancora un approccio 'ognuno per sé'?",
          ),
          t(
            "Spot-check outputs against the SOP; add adherence as a KPI in team reviews.",
            "Pārbaudiet rezultātus pret SOP; pievienojiet ievērošanu kā KPI komandas pārskatos.",
            "Verificare a campione i risultati rispetto al SOP; aggiungere l'aderenza come KPI nelle revisioni del team.",
          ),
        ),
        item(
          t("Risk Management", "Riska Pārvaldība", "Gestione del Rischio"),
          REQUIRED,
          t(
            "Are exceptions defined? Is decision-making documented for alternatives?",
            "Vai izņēmumi ir definēti? Vai lēmumu pieņemšana alternatīvām ir dokumentēta?",
            "Le eccezioni sono definite? Il processo decisionale per le alternative è documentato?",
          ),
          t(
            "e.g., 'If approval is denied → escalate to CFO within 2 hours'; document the decision tree.",
            "piem., 'Ja apstiprinājums tiek atteikts → eskalēt uz CFO 2 stundu laikā'; dokumentējiet lēmumu koku.",
            "es., 'Se l'approvazione viene negata → escalate al CFO entro 2 ore'; documentare l'albero decisionale.",
          ),
        ),
      ],
    },
    {
      title: t(
        "1.3. Process Strategy",
        "1.3. Procesa Stratēģija",
        "1.3. Strategia di Processo",
      ),
      items: [
        item(
          t(
            "Process Broader Context",
            "Procesa Plašāks Konteksts",
            "Contesto Più Ampio del Processo",
          ),
          RECOMMENDED,
          t(
            "Are existing initiatives and continuous improvement efforts factored into how the process operates?",
            "Vai esošās iniciatīvas un nepārtrauktas uzlabošanas centieni ir ņemti vērā procesa darbībā?",
            "Le iniziative esistenti e gli sforzi di miglioramento continuo sono considerati nel funzionamento del processo?",
          ),
          t(
            "Map the process against ongoing projects and strategic goals; review quarterly for alignment.",
            "Kartējiet procesu pret notiekošajiem projektiem un stratēģiskajiem mērķiem; pārskatiet reizi ceturksnī, lai nodrošinātu atbilstību.",
            "Mappare il processo rispetto ai progetti in corso e agli obiettivi strategici; verificare trimestralmente l'allineamento.",
          ),
        ),
        item(
          t("Process Future", "Procesa Nākotne", "Futuro del Processo"),
          REQUIRED,
          t(
            "Is the process designed with future adaptability in mind?",
            "Vai process ir izstrādāts ar nākotnes pielāgojamību prātā?",
            "Il processo è progettato con la futura adattabilità in mente?",
          ),
          t(
            "Anticipate upcoming regulations (GDPR, AI Act), org restructuring, or expected volume changes when designing the flow.",
            "Paredziet gaidāmos noteikumus (GDPR, AI Act), organizatorisko pārstrukturēšanu vai sagaidāmās apjoma izmaiņas, izstrādājot plūsmu.",
            "Anticipare le prossime normative (GDPR, AI Act), la ristrutturazione organizzativa o le variazioni di volume previste durante la progettazione del flusso.",
          ),
        ),
      ],
    },
  ],
};
